
import { memo, useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";
import { createPortal } from "react-dom";
import { CodeModal } from "./modals/CodeModal";

interface StepCodeBlockProps {
  icon: ReactNode;
  label: string;
  code: string;
  title?: string;
  language?: "javascript" | "typescript" | "json";
}

function StepCodeBlockComponent({ icon, label, code, title, language }: StepCodeBlockProps) {
  const t = useTranslations("workflows");
  const [showModal, setShowModal] = useState(false);

  const truncatedCode = code.length > 150 ? code.slice(0, 150) + "..." : code;
  const needsTruncation = code.length > 150 || code.split("\n").length > 3;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowModal(true);
  };

  return (
    <>
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="shrink-0 text-gray-400">{icon}</span>
          <span className="text-gray-500 dark:text-gray-400">{label}</span>
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.key === "Enter" && setShowModal(true)}
          className="nopan nodrag nowheel pointer-events-auto w-full cursor-pointer rounded border border-gray-200 bg-gray-50 p-2 text-left transition-colors hover:border-brand-400 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
        >
          <pre className="pointer-events-none m-0 line-clamp-3 whitespace-pre-wrap font-mono text-[11px] text-gray-700 wrap-break-word dark:text-gray-300">
            {truncatedCode}
          </pre>
          {needsTruncation && (
            <span className="pointer-events-none mt-1 block text-[10px] text-brand-500">{t("flow.code_view_full")}</span>
          )}
        </div>
      </div>

      {showModal && createPortal(
        <CodeModal code={code} title={title || label} language={language} onClose={() => setShowModal(false)} />,
        document.body
      )}
    </>
  );
}

export const StepCodeBlock = memo(StepCodeBlockComponent);
